import React, { useContext, useState } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import axios from 'axios';
import translate from '../../../translate';
import UserProp from '../../../propTypes/userProp';
import CountryProp from '../../../forms/Address/CountryProp';
import Notifications from '../../../../services/notifications';
import withCSRF from '../../../withCSRF';
import { compose } from '../../../../services/utilityFunctions';
import LoggedUserContext from '../../../../contextProviders/LoggedUserContext';
import MarketInfosContext from '../../../../contextProviders/MarketInfosContext';

function LanguageSelector({ t, _csrf, profile, languages, bravonLanguages }) {
  const [langFilter, setLangFilter] = useState('');
  const { loggedUser } = useContext(LoggedUserContext);
  const { setMarketInfos, marketInfos } = useContext(MarketInfosContext);
  const handleLangFilterChange = ({ currentTarget }) =>
    setLangFilter(currentTarget.value);
  const handleLanguageSelection = language => {
    const currentLang = marketInfos?.localLanguage;
    if (language === currentLang) {
      return;
    }
    axios
      .put(
        '/api/user/me/settings',
        { language },
        {
          headers: {
            'Accept-Version': '1.0.0',
            'X-Gamification-API': 'Organization',
            'X-CSRF-Token': _csrf,
            _csrf,
          },
        },
      )
      .then(() => {
        setMarketInfos('localLanguage', language);
        Notifications.showNotificationSuccess(
          t('success'),
          t('success_update'),
        );
        window.location.reload();
      })
      .catch(err => {
        Notifications.showErrorFromRequest(err, 'error_profileUpdate');
      });
  };

  const handleLanguageLocalStorage = language => {
    const currentLang = marketInfos?.localLanguage;
    setMarketInfos('localLanguage', language);
    if (language !== currentLang) {
      // Redirect to the correct path, with the correct languagePrefix
      let languageToUpdate;
      if (language === 'en') {
        // eslint-disable-next-line prefer-destructuring
        languageToUpdate = window.location.pathname.split(currentLang)[1];
      } else {
        languageToUpdate = window.location.pathname.replace(
          bravonLanguages,
          language,
        );
      }
      window.location = languageToUpdate;
    }
  };

  const filteredLanguages = langFilter
    ? languages.filter(l =>
        l.native.toUpperCase().includes(langFilter.toUpperCase()),
      )
    : languages;

  const profileLanguage = profile && profile.language ? profile.language : 'en';
  const selectedLanguage =
    profileLanguage && languages && languages.length > 0
      ? languages.find(l => l.code === profileLanguage) || {}
      : {};
  const selectedFlag =
    selectedLanguage && selectedLanguage.flag
      ? selectedLanguage.flag.thumb
      : null;
  const selectedCode =
    selectedLanguage && selectedLanguage.code
      ? selectedLanguage.code
      : profileLanguage;

  return (
    <li
      /* eslint-disable-next-line max-len */
      className="m-nav__item m-topbar__languages m-dropdown m-dropdown--small m-dropdown--arrow m-dropdown--align-right m-dropdown--mobile-full-width"
      m-dropdown-toggle="click"
      aria-expanded="false"
      style={{ marginRight: '1rem' }}
    >
      <a href="#" className="m-nav__link m-dropdown__toggle">
        <span className="m-nav__link-text">
          {selectedFlag ? (
            <img
              alt="language-preview"
              className="m-topbar__language-selected-img"
              src={selectedFlag}
            />
          ) : (
            (selectedCode || 'en').toUpperCase()
          )}
        </span>
      </a>
      <div
        className="m-dropdown__wrapper"
        style={{ zIndex: '101' }}
        id="languageSelector"
      >
        <span
          className="m-dropdown__arrow m-dropdown__arrow--right m-dropdown__arrow--adjust"
          style={{ left: 'auto', right: '5px' }}
        />
        <div className="m-dropdown__inner">
          <div className="m-dropdown__header m--align-center">
            <span className="m-dropdown__header-subtitle">
              {t('select_language')}
            </span>
          </div>
          <div className="m-dropdown__body">
            <input
              type="text"
              placeholder={t('find_language')}
              value={langFilter}
              onChange={handleLangFilterChange}
              className="form-control m-input"
              style={{ marginBottom: '1rem' }}
            />
            <div
              className="m-dropdown__content"
              style={{
                maxHeight: '600px',
                overflowY: 'auto',
              }}
            >
              <ul className="m-nav m-nav--skin-light">
                {filteredLanguages.map(l => (
                  <li
                    key={l.code}
                    className={cx({
                      'm-nav__item': true,
                      'm-nav__item--active': l.code === selectedLanguage.code,
                    })}
                  >
                    <a
                      href="#"
                      onClick={() =>
                        loggedUser
                          ? handleLanguageSelection(l.code)
                          : handleLanguageLocalStorage(l.code)
                      }
                      className={cx({
                        'm-nav__link': true,
                        'm-nav__link--active': l.code === selectedLanguage.code,
                      })}
                    >
                      <span className="m-nav__link-icon">
                        {l.flag && l.flag.thumb ? (
                          <img
                            alt="language flag"
                            className="m-topbar__language-img"
                            src={l.flag.thumb}
                            loading="lazy"
                          />
                        ) : (
                          l.code.toUpperCase()
                        )}
                      </span>
                      <span className="m-nav__link-title m-topbar__language-text m-nav__link-text">
                        {l.native}
                      </span>
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </li>
  );
}

LanguageSelector.propTypes = {
  t: PropTypes.func.isRequired,
  _csrf: PropTypes.string.isRequired,
  profile: PropTypes.shape(UserProp.propType).isRequired,
  languages: PropTypes.arrayOf(PropTypes.shape(CountryProp.propType))
    .isRequired,
  bravonLanguages: PropTypes.arrayOf(PropTypes.string).isRequired,
};

export default compose(translate, withCSRF)(LanguageSelector);
